import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Flag, Loader2, ChevronLeft, CreditCard, Truck, BadgeCheck, Wallet, Receipt, Mail, MessageSquare, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";
import { FEES, FEES_DISCLAIMER } from "@/config/fees";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function useSEO(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    const prevDesc = tag.getAttribute("content");
    tag.setAttribute("content", description);
    return () => { document.title = prev; if (prevDesc) tag.setAttribute("content", prevDesc); };
  }, [title, description]);
}

function Shell({ title, children, testid }) {
  return (
    <div>
      <div className="mx-auto max-w-3xl px-4 pb-16 pt-8 md:px-8" data-testid={testid}>
        <h1 className="mb-6 font-display text-4xl text-[#F5F5F5] sm:text-5xl">{title}</h1>
        <div className="space-y-4 leading-relaxed text-[#cfcfcf]">{children}</div>
      </div>
      <Footer />
    </div>
  );
}
function H({ children }) { return <h2 className="mt-6 font-display text-xl text-[#F5F5F5]">{children}</h2>; }

export function Voorwaarden() {
  return (
    <Shell title="ALGEMENE VOORWAARDEN" testid="page-voorwaarden">
      <p>Welkom bij VRIJMARKT. Door gebruik te maken van ons platform ga je akkoord met deze voorwaarden.</p>
      <H>1. Het platform</H><p>VRIJMARKT is een marktplaats waarop particulieren en bedrijven tweedehands en nieuwe artikelen kunnen aanbieden en kopen. VRIJMARKT is geen partij bij de koopovereenkomst tussen koper en verkoper.</p>
      <H>2. Advertenties</H><p>Je bent zelf verantwoordelijk voor de inhoud van je advertenties. Verboden zijn illegale, misleidende of gevaarlijke producten. VRIJMARKT mag advertenties verwijderen die in strijd zijn met deze voorwaarden.</p>
      <H>3. Gedrag</H><p>Behandel andere gebruikers met respect. Fraude, spam en misbruik leiden tot schorsing of verwijdering van je account.</p>
      <H>4. Aansprakelijkheid</H><p>VRIJMARKT is niet aansprakelijk voor schade voortvloeiend uit transacties tussen gebruikers. Controleer altijd het product en de verkoper.</p>
      <H>5. Wijzigingen</H><p>We kunnen deze voorwaarden aanpassen. Belangrijke wijzigingen communiceren we via het platform.</p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="PRIVACYBELEID" testid="page-privacy">
      <p>VRIJMARKT respecteert je privacy en verwerkt persoonsgegevens conform de AVG.</p>
      <H>Welke gegevens</H><p>We verwerken je naam, e-mailadres, locatie (indien opgegeven) en de gegevens die je in advertenties en chats plaatst.</p>
      <H>Waarvoor</H><p>Voor het aanbieden van het platform, het tonen van advertenties in de buurt, communicatie en fraudepreventie.</p>
      <H>Delen</H><p>We verkopen je gegevens niet. Gegevens worden alleen gedeeld met dienstverleners (zoals betaal- en e-mailpartners) voor zover nodig.</p>
      <H>Jouw rechten</H><p>Je hebt recht op inzage, correctie en verwijdering. Je kunt je account op elk moment verwijderen via je profielinstellingen. Vragen? Mail <a href="mailto:info@vrijmarkt.store" className="text-[#FF6A00]">info@vrijmarkt.store</a>.</p>
    </Shell>
  );
}

export function Cookies() {
  return (
    <Shell title="COOKIES" testid="page-cookies">
      <p>VRIJMARKT gebruikt functionele cookies en lokale opslag om je ingelogd te houden en de app-ervaring te verbeteren.</p>
      <H>Functioneel</H><p>Noodzakelijk om de site te laten werken, zoals het onthouden van je sessie.</p>
      <H>Analytisch</H><p>We gebruiken geanonimiseerde statistieken om de site te verbeteren. Deze zijn niet herleidbaar tot jou persoonlijk.</p>
    </Shell>
  );
}

export function VeiligHandelen() {
  useSEO(
    "Veilig en duidelijk handelen — VRIJMARKT",
    "Zo werkt veilig kopen en verkopen op VRIJMARKT: gratis plaatsen, veilig betalen via Mollie, duidelijke kosten vooraf, ophalen of verzenden en contact."
  );
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);
  return (
    <div>
      <div className="mx-auto max-w-4xl px-4 pb-16 pt-8 md:px-8" data-testid="page-veilig-handelen">
        <Link to="/" data-testid="veilig-back-btn" className="mb-6 inline-flex items-center gap-1 text-sm text-[#A0A0A0] transition-colors hover:text-[#FF6A00]"><ChevronLeft className="h-4 w-4" /> Terug naar aanbod</Link>

        <h1 className="font-display text-4xl text-[#F5F5F5] sm:text-5xl lg:text-6xl">VEILIG EN DUIDELIJK HANDELEN</h1>
        <p className="mt-4 max-w-2xl text-[#cfcfcf]">Bij VRIJMARKT houden we kopen en verkopen graag eenvoudig. Advertenties plaatsen is gratis. Wanneer je kiest voor online betalen, kunnen er transactiekosten gelden. Deze kosten worden altijd duidelijk getoond voordat een betaling wordt bevestigd.</p>

        {/* Sectie 1 */}
        <Card id="gratis-plaatsen" icon={Receipt} title="Gratis advertenties plaatsen">
          <p>Particuliere gebruikers kunnen gratis een advertentie plaatsen. Er worden geen plaatsingskosten of abonnementskosten gerekend voor normale particuliere advertenties.</p>
          <p>VRIJMARKT kan later aparte tarieven invoeren voor zakelijke accounts, extra zichtbaarheid, voertuigen of aanvullende premiumdiensten. Wanneer een betaalde optie wordt aangeboden, moet de prijs altijd vooraf duidelijk worden weergegeven.</p>
        </Card>

        {/* Sectie 2 */}
        <Card id="eerlijk-veilig" icon={ShieldCheck} title="Veilig contact en betalen">
          <p>Kopers en verkopers kunnen via de interne chat afspraken maken. Deel geen wachtwoorden, verificatiecodes, herstelcodes of bankgegevens via de chat.</p>
          <p>Bij online betalingen wordt de betaling verwerkt via Mollie. De betaalstatus wordt uitsluitend server-side via de Mollie-webhook bevestigd. Een browserredirect wordt nooit op zichzelf als bewijs van een geslaagde betaling gebruikt.</p>
          <ul className="mt-2 space-y-2">
            <Warn>Betaal niet buiten VRIJMARKT wanneer je gebruik wilt maken van de ingebouwde betaalbeveiliging.</Warn>
            <Warn>Controleer het product en de verkoper voordat je betaalt.</Warn>
            <Warn>Spreek bij ophalen bij voorkeur af op een veilige openbare locatie.</Warn>
            <Warn>Meld verdachte advertenties of gebruikers.</Warn>
          </ul>
          <a href="mailto:support@vrijmarkt.store?subject=Melding%20verdachte%20activiteit" data-testid="report-activity-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF6A00] px-5 py-2.5 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95"><Flag className="h-4 w-4" /> Meld verdachte activiteit</a>
        </Card>

        {/* Sectie 3 */}
        <Card id="echte-particulieren" icon={BadgeCheck} title="Accounts en verificatie">
          <p>Gebruikers melden zich aan met naam, e-mailadres en wachtwoord. Het e-mailadres wordt geverifieerd voordat alle functies beschikbaar zijn.</p>
          <p>In de toekomst worden aanvullende verificatiestappen toegevoegd, zoals telefoonnummerverificatie, identiteitscontrole, bankrekeningcontrole, een verkopersbadge en beoordelingen met accountgeschiedenis.</p>
          <p className="rounded-lg border border-[#333] bg-[#111] p-3 text-sm text-[#A0A0A0]">VRIJMARKT controleert accounts met beschikbare verificatiemethoden. Aanvullende identiteits- en verkopersverificatie wordt stapsgewijs toegevoegd.</p>
        </Card>

        {/* Sectie 4 */}
        <Card id="geen-verborgen-kosten" icon={Wallet} title="Duidelijke kosten vooraf">
          <p>Advertenties plaatsen is gratis. Bij gebruik van online betalen kunnen kosten van de betaalprovider en VRIJMARKT van toepassing zijn. Deze kosten worden vóór het bevestigen van de betaling afzonderlijk en duidelijk getoond.</p>
          <div className="mt-4 space-y-4" data-testid="fees-table">
            {Object.entries(FEES).map(([key, group]) => (
              <div key={key} className="overflow-hidden rounded-xl border border-[#333]">
                <div className="flex items-center gap-2 bg-[#2A2A2A] px-4 py-2.5 font-display text-sm tracking-wide text-[#F5F5F5]"><CreditCard className="h-4 w-4 text-[#FF6A00]" /> {group.label.toUpperCase()}</div>
                <table className="w-full text-sm">
                  <tbody>
                    {group.rows.map(([k, v]) => (
                      <tr key={k} className="border-t border-[#222]"><td className="w-1/2 px-4 py-2.5 text-[#A0A0A0]">{k}</td><td className="px-4 py-2.5 text-[#F5F5F5]">{v}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#777]">{FEES_DISCLAIMER}</p>
        </Card>

        {/* Sectie 5 */}
        <Card id="betalingen-reserveringen" icon={ShieldCheck} title="Wat gebeurt er na een betaling?">
          <p>Na een geslaagde betaling:</p>
          <ul className="ml-5 list-disc space-y-1 text-[#cfcfcf]">
            <li>wordt de betaling via de Mollie-webhook bevestigd</li>
            <li>krijgt de koper een bevestiging</li>
            <li>wordt de advertentie gemarkeerd als gereserveerd</li>
            <li>ontvangt de verkoper een melding</li>
            <li>blijft de transactie in het accountoverzicht zichtbaar</li>
          </ul>
          <p>Bij een mislukte, verlopen of geannuleerde betaling wordt de advertentie niet als betaald gemarkeerd.</p>
        </Card>

        {/* Sectie 6 */}
        <Card id="ophalen-verzenden" icon={Truck} title="Ophalen of verzenden">
          <p>De verkoper geeft per advertentie aan of het product kan worden opgehaald, verzonden of beide.</p>
          <p>Bij verzending:</p>
          <ul className="ml-5 list-disc space-y-1 text-[#cfcfcf]">
            <li>spreek vooraf af wie de verzendkosten betaalt</li>
            <li>gebruik bij voorkeur Track &amp; Trace</li>
            <li>bewaar het verzendbewijs</li>
            <li>controleer het afleveradres</li>
          </ul>
          <p className="text-sm text-[#777]">Track &amp; Trace en automatische uitbetaling aan verkopers worden later uitgebreid.</p>
        </Card>

        {/* Sectie 7 */}
        <Card id="contact" icon={Mail} title="Hulp nodig?">
          <div className="grid gap-2 sm:grid-cols-3">
            <ContactCard label="Algemene informatie" email="info@vrijmarkt.store" />
            <ContactCard label="Ondersteuning en problemen" email="support@vrijmarkt.store" />
            <ContactCard label="Beheer en officiële communicatie" email="admin@vrijmarkt.store" />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="veilig-contact-btn" className="inline-flex items-center gap-2 rounded-full bg-[#FF6A00] px-5 py-2.5 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95"><MessageSquare className="h-4 w-4" /> Neem contact op</Link>
            <a href="mailto:support@vrijmarkt.store?subject=Probleem%20melden" data-testid="veilig-report-btn" className="inline-flex items-center gap-2 rounded-full border border-[#333] px-5 py-2.5 font-semibold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]"><Flag className="h-4 w-4" /> Meld een probleem</a>
            <Link to="/zoeken" data-testid="veilig-browse-btn" className="inline-flex items-center gap-2 rounded-full border border-[#333] px-5 py-2.5 font-semibold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]"><ArrowLeft className="h-4 w-4" /> Terug naar aanbod</Link>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}

function Card({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="mt-8 scroll-mt-24 rounded-2xl border border-[#333] bg-[#1c1c1c] p-5 md:p-7">
      <h2 className="flex items-center gap-2 font-display text-2xl text-[#F5F5F5]"><Icon className="h-6 w-6 text-[#FF6A00]" /> {title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-[#cfcfcf]">{children}</div>
    </section>
  );
}
function Warn({ children }) { return <li className="flex items-start gap-2 text-[#cfcfcf]"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#FFB27A]" /> <span>{children}</span></li>; }
function ContactCard({ label, email }) { return <a href={`mailto:${email}`} className="block rounded-xl border border-[#333] bg-[#111] p-3 transition-colors hover:border-[#FF6A00]"><p className="text-xs text-[#A0A0A0]">{label}</p><p className="font-semibold text-[#FF6A00]">{email}</p></a>; }

const FAQS = [
  ["Is VRIJMARKT gratis?", "Ja, een account aanmaken en advertenties plaatsen is volledig gratis. Er zijn geen verborgen kosten."],
  ["Hoe plaats ik een advertentie?", "Klik op 'Plaatsen', voeg foto's toe en laat eventueel de AI-agent een titel, categorie, beschrijving en prijs voorstellen. Controleer alles en publiceer."],
  ["Hoe werkt de AI advertentie-agent?", "Upload een of meer foto's en klik op 'Genereer met AI'. De AI stelt een advertentie voor die je zelf kunt aanpassen voordat je publiceert."],
  ["Hoe neem ik contact op met een verkoper?", "Open een advertentie en klik op 'Chat met verkoper' om direct een bericht te sturen."],
  ["Hoe betaal ik veilig?", "Online betalen via iDEAL en creditcard wordt binnenkort geactiveerd. Betaal nooit buiten een ondersteunde methode om."],
  ["Kan ik mijn account verwijderen?", "Ja, via Profiel → Instellingen kun je je account en al je advertenties permanent verwijderen."],
];

export function FAQ() {
  return (
    <Shell title="VEELGESTELDE VRAGEN" testid="page-faq">
      <Accordion type="single" collapsible className="w-full">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`item-${i}`} className="border-[#333]">
            <AccordionTrigger className="text-left font-semibold text-[#F5F5F5] hover:text-[#FF6A00]">{q}</AccordionTrigger>
            <AccordionContent className="text-[#A0A0A0]">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Shell>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const inp = "w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-[#F5F5F5] outline-none focus:border-[#FF6A00]";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/support", f); setSent(true); toast.success("Bericht verstuurd"); }
    catch { toast.error("Versturen mislukt"); } finally { setBusy(false); }
  };
  return (
    <Shell title="CONTACT" testid="page-contact">
      <p>Vragen of hulp nodig? Mail <a href="mailto:support@vrijmarkt.store" className="text-[#FF6A00]">support@vrijmarkt.store</a> of gebruik het formulier.</p>
      {sent ? (
        <div className="rounded-xl border border-[#22C55E]/40 bg-[#22C55E]/10 p-4 text-[#22C55E]" data-testid="contact-sent">Bedankt! We nemen zo snel mogelijk contact met je op.</div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <input required placeholder="Naam" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="contact-name" />
          <input required type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="contact-email" />
          <textarea required rows={5} placeholder="Je bericht" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={`${inp} resize-none`} data-testid="contact-message" />
          <button type="submit" disabled={busy} data-testid="contact-submit" className="flex items-center gap-2 rounded-full bg-[#FF6A00] px-6 py-3 font-bold text-[#111]">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Versturen</button>
        </form>
      )}
    </Shell>
  );
}
